import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useCognitoCheckUser } from '@/entities/user/currentUser';
import { setAuthForm, setViewerEmail, setViewerPassword, useViewerStore } from '@/entities/viewer';

import { AUTH_API } from '@/shared/api/amplify';
import { PATHS } from '@/shared/constants/routes/paths';
import { useCountdown } from '@/shared/hooks/useCountdown';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { AuthForm } from '@/shared/types/Cognito';
import { ControlledTextField } from '@/shared/ui/form/TextField';

import { FormContainer } from '../../components/FormContainer';
import { GoBackLink } from '../../components/GoBackLink';
import { Link } from '../../components/Link/styled';
import { SubmitButton } from '../../components/SubmitButton/styled';
import { DATA_TEST_IDS } from '../../lib/constants';
import { Container, LinkContainer } from './styled';

const TIME_BEFORE_NEXT_ATTEMPT_SEC = 59;

type FormValues = {
  code: string;
};

export const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { refetch: refetchCognitoUser } = useCognitoCheckUser({ enabled: false });

  const email = useViewerStore((state) => state.email) || '';
  const password = useViewerStore((state) => state.password) || '';

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  const { count, startCountdown } = useCountdown(TIME_BEFORE_NEXT_ATTEMPT_SEC);

  const onSubmit = async (input: FormValues) => {
    try {
      if (!email) {
        return;
      }

      setIsLoading(true);

      const { code } = input;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const confirmResponse = await AUTH_API.confirmSignUp(email, code);

      if (!confirmResponse) {
        throw new Error('Something went wrong');
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const signInResponse = await AUTH_API.signIn(email, window.atob(password));
      setViewerEmail(undefined);
      setViewerPassword(undefined);

      if (signInResponse) {
        await refetchCognitoUser();
        navigate(PATHS.homepage);
        reset();
      }
    } catch (e) {
      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmationCode = async () => {
    try {
      await AUTH_API.resendConfirmationCode(email);
    } catch (e) {
      showErrorAlert(e);
    } finally {
      startCountdown();
      setIsLoading(false);
    }
  };

  const switchToSignUpForm = () => {
    setAuthForm(AuthForm.Signup);
  };

  return (
    <FormContainer
      title="Confirm your email"
      subtitle={`We sent an email with code to ${email}.  Copy the code from the email and paste in the form to confirm your account.`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Container>
        <ControlledTextField
          label="Code"
          slotProps={{
            input: {
              inputProps: {
                autoComplete: 'off',
              },
            },
          }}
          control={control}
          disabled={isLoading}
          fieldName="code"
          placeholder="Code"
          showEndAdornmentOnError={false}
          variant="outlined"
        />

        <SubmitButton
          data-testid={DATA_TEST_IDS.confirmEmail}
          disabled={isLoading}
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Confirm email
        </SubmitButton>
        <LinkContainer>
          <GoBackLink data-testid={DATA_TEST_IDS.signInButton} text="Back to Sign up" onClick={switchToSignUpForm} />
          <Link
            data-testid={DATA_TEST_IDS.resendCodeButton}
            disabled={isLoading || Boolean(count)}
            onClick={resendConfirmationCode}
          >
            {!count || isLoading ? 'Resend code' : `Resend again in ${count} second${count === 1 ? '' : 's'}`}
          </Link>
        </LinkContainer>
      </Container>
    </FormContainer>
  );
};
